import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import './App.css';
import { Toaster } from './components/ui/sonner';
import { isAuthenticated } from './utils/auth';
import InstallPWA from './components/InstallPWA';
import LiveChatWidget from './components/LiveChatWidget';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ClearCachePage from './pages/ClearCachePage';
import VersionCheckPage from './pages/VersionCheckPage';
import EnhancedDashboardPage from './pages/EnhancedDashboardPage';
import CasesPage from './pages/CasesPage';
import DocumentsPage from './pages/DocumentsPage';
import NotificationsPage from './pages/NotificationsPage';
import ProfilePage from './pages/ProfilePage';
// Admin Portal - بوابة الإدارة (طبقة جديدة)
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboardNew from './pages/admin/AdminDashboard';
import PlaceholderPage from './pages/admin/PlaceholderPage';
import ProtectedAdminRoute from './components/admin/ProtectedAdminRoute';
import InvoicesPage from './pages/admin/finance/InvoicesPage';
import InvoiceDetailsPage from './pages/admin/finance/InvoiceDetailsPage';
import InvoiceFormPage from './pages/admin/finance/InvoiceFormPage';
import OverdueInvoicesPage from './pages/admin/finance/OverdueInvoicesPage';
import CreditorsPage from './pages/admin/finance/CreditorsPage';
import CreditorDetailsPage from './pages/admin/finance/CreditorDetailsPage';
import CreditorFormPage from './pages/admin/finance/CreditorFormPage';
import InvoiceSettingsPage from './pages/admin/finance/InvoiceSettingsPage';
import RevenuesPage from './pages/admin/finance/RevenuesPage';
import RevenueFormPage from './pages/admin/finance/RevenueFormPage';
import ExpensesPage from './pages/admin/finance/ExpensesPage';
import ExpenseFormPage from './pages/admin/finance/ExpenseFormPage';
import SalariesPage from './pages/admin/finance/SalariesPage';
import SalaryDetailsPage from './pages/admin/finance/SalaryDetailsPage';
import SalaryFormPage from './pages/admin/finance/SalaryFormPage';
import PayrollBatchesPage from './pages/admin/finance/PayrollBatchesPage';
import FinancialReportsPage from './pages/admin/finance/FinancialReportsPage';
import RevenueReportsPage from './pages/admin/finance/RevenueReportsPage';
import ExpenseReportsPage from './pages/admin/finance/ExpenseReportsPage';
import ProfitabilityReportsPage from './pages/admin/finance/ProfitabilityReportsPage';
import PayrollReportsPage from './pages/admin/finance/PayrollReportsPage';
import CollectionReportsPage from './pages/admin/finance/CollectionReportsPage';
// HR Pages
import EmployeesListPage from './pages/admin/hr/EmployeesListPage';
import EmployeeFormPage from './pages/admin/hr/EmployeeFormPage';
import EmployeeProfilePage from './pages/admin/hr/EmployeeProfilePage';
import AttendancePage from './pages/admin/hr/AttendancePage';
import LeavesPage from './pages/admin/hr/LeavesPage';
import CertificatesPage from './pages/admin/hr/CertificatesPage';
import PerformanceReviewsPage from './pages/admin/hr/PerformanceReviewsPage';
import PerformanceReviewFormPage from './pages/admin/hr/PerformanceReviewFormPage';
import LaborIntakeAdminPage from './pages/admin/labor/LaborIntakeAdminPage';
import LaborIntakeAdminDetailsPage from './pages/admin/labor/LaborIntakeAdminDetailsPage';
import LaborOperationsLoginPage from './pages/admin/labor/LaborOperationsLoginPage';
import LegalOperationsModelPage from './pages/admin/labor/LegalOperationsModelPage';
import LegalOperationsUsersPage from './pages/admin/labor/LegalOperationsUsersPage';
import LegalOperationsSettingsPage from './pages/admin/labor/LegalOperationsSettingsPage';
import LegalOperationsMessagesPage from './pages/admin/labor/LegalOperationsMessagesPage';
import ProtectedLaborOperationsRoute from './components/labor/ProtectedLaborOperationsRoute';
import { Scale, Users, DollarSign, Briefcase, Headphones, CheckCircle, Cog, BarChart3, Settings } from 'lucide-react';

// Old admin pages (keeping for backward compatibility)
import AdminDashboard from './pages/admin/AdminDashboard';
import HRManagement from './pages/admin/HRManagement';
import FinanceManagement from './pages/admin/FinanceManagement';
import TicketsManagement from './pages/admin/TicketsManagement';
import UsersManagement from './pages/admin/UsersManagement';
import LegalLibrary from './pages/admin/LegalLibrary';
import Correspondence from './pages/admin/Correspondence';
import ContentManagement from './pages/admin/ContentManagement';
import ReportsAnalytics from './pages/admin/ReportsAnalytics';
import SystemSettings from './pages/admin/SystemSettings';
import DepartmentsManagement from './pages/admin/DepartmentsManagement';
import PackagesManagement from './pages/admin/PackagesManagement';
import CasesManagement from './pages/admin/CasesManagement';
import LawyerDashboard from './pages/LawyerDashboard';
import ChatPage from './pages/ChatPage';
import AdvancedCaseManagement from './pages/admin/AdvancedCaseManagement';
import CaseDetailsPage from './pages/admin/CaseDetailsPage';
import TicketsManagementPage from './pages/TicketsManagementPage';
import CreateTicketPage from './pages/CreateTicketPage';
import TicketDetailsPage from './pages/TicketDetailsPage';
import RequestsListPage from './pages/hr/RequestsListPage';
import CreateLeaveRequestPage from './pages/hr/CreateLeaveRequestPage';
import RequestDetailsPage from './pages/hr/RequestDetailsPage';
import UnifiedLoginPagePro from './pages/UnifiedLoginPagePro';
import ModernDashboard from './pages/ModernDashboard';
import LiveChatsPage from './pages/support/LiveChatsPage';
import RolesPermissionsPage from './pages/admin/RolesPermissionsPage';
import EmployeePermissionsPage from './pages/admin/EmployeePermissionsPage';
import EmployeeCustomPermissionsPage from './pages/admin/EmployeeCustomPermissionsPage';
import PermissionProtectedRoute from './components/PermissionProtectedRoute';
import ContractGenerationPage from './pages/admin/ContractGenerationPage';
import ContractsListPage from './pages/admin/ContractsListPage';
import ContractTemplatesPage from './pages/admin/ContractTemplatesPage';
import ContractTemplateFormPage from './pages/admin/ContractTemplateFormPage';
import SignContractPage from './pages/client/SignContractPage';
import LawyerSignContractPage from './pages/admin/LawyerSignContractPage';
import ContractAuditLogPage from './pages/admin/ContractAuditLogPage';
import NotificationsCenterPage from './pages/NotificationsCenterPage';
import ContractVerificationPage from './pages/ContractVerificationPage';
import PublicSignContractPage from './pages/PublicSignContractPage';
import ServicesPage from './pages/ServicesPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';

// Client Portal Pages
import ClientDashboard from './pages/client/ClientDashboardProfessional';
import ClientCasesPage from './pages/client/ClientCasesPage';
import ClientCaseDetailsPage from './pages/client/ClientCaseDetailsPage';
import ClientConsultationsPage from './pages/client/ClientConsultationsPage';
import ClientTicketsPage from './pages/client/ClientTicketsPage';
import ClientCaseDetailsNewPage from './pages/client/CaseDetailsPage';

// Business Portal Pages
import BusinessDashboard from './pages/business/BusinessDashboard';
import BusinessCaseDetailsPage from './pages/business/BusinessCaseDetailsPage';

// Services Pages
import ServicesOverview from './pages/services/ServicesOverview';
import AllServicesPage from './pages/services/AllServicesPage';
import LitigationPage from './pages/services/LitigationPage';
import ConsultationPage from './pages/services/ConsultationPage';
import ContractsPage from './pages/services/ContractsPage';
import NotarizationPage from './pages/services/NotarizationPage';
import CollectionPage from './pages/services/CollectionPage';
import CybercrimePage from './pages/services/CybercrimePage';
import CompliancePage from './pages/services/CompliancePage';
import IPPage from './pages/services/IPPage';
import FranchisePage from './pages/services/FranchisePage';

// Packages Pages
import PackagesPage from './pages/packages/PackagesPage';
import ComparePackagesPage from './pages/packages/ComparePackagesPage';

// Booking Pages
import BookConsultationPage from './pages/booking/BookConsultationPage';
import LaborIntakePage from './pages/labor/LaborIntakePage';
import LaborIntakeEntryPage from './pages/labor/LaborIntakeEntryPage';
import LaborIntakeDraftPage from './pages/labor/LaborIntakeDraftPage';
import LaborIntakeSuccessPage from './pages/labor/LaborIntakeSuccessPage';
import LaborIntakeTrackingPage from './pages/labor/LaborIntakeTrackingPage';

// Divisions Page
import DivisionsPage from './pages/DivisionsPage';

// User Type Selection Page
import SelectUserTypePage from './pages/SelectUserTypePage';

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" />;
  }
  
  // If no roles specified, allow any authenticated user
  if (allowedRoles.length === 0) {
    return children;
  }
  
  // Check user role
  const userStr = localStorage.getItem('user');
  if (!userStr) {
    return <Navigate to="/login" />;
  }
  
  const user = JSON.parse(userStr);
  
  // If user role is in allowed roles, grant access
  if (allowedRoles.includes(user.role)) {
    return children;
  }
  
  // Otherwise, redirect based on role and type
  console.log('🛡️ ProtectedRoute - User:', user);
  console.log('🛡️ ProtectedRoute - Role:', user.role);
  console.log('🛡️ ProtectedRoute - Client Type:', user.client_type);
  console.log('🛡️ ProtectedRoute - User Type:', user.user_type);
  
  if (user.role === 'client') {
    // Check client type for proper routing
    if (user.client_type === 'business' || user.user_type === 'business') {
      console.log('🛡️ ProtectedRoute - Redirecting to /business/dashboard');
      return <Navigate to="/business/dashboard" />;
    } else {
      console.log('🛡️ ProtectedRoute - Redirecting to /client/dashboard');
      return <Navigate to="/client/dashboard" />;
    }
  } else if (user.role === 'lawyer' && user.user_type === 'staff') {
    console.log('🛡️ ProtectedRoute - Redirecting to /admin/labor-intake');
    return <Navigate to="/admin/labor-intake" />;
  } else {
    console.log('🛡️ ProtectedRoute - Redirecting to /dashboard');
    return <Navigate to="/dashboard" />;
  }
};

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Set RTL for Arabic
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <AdminAuthProvider>
      <div className="App">
        <InstallPWA />
        <BrowserRouter>
          <Routes>
          {/* Default public entry point: labor intake client portal */}
          <Route path="/" element={<Navigate to="/labor-intake" replace />} />
          
          {/* Login Routes */}
          <Route path="/portals" element={<UnifiedLoginPagePro />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/clear-cache" element={<ClearCachePage />} />
          <Route path="/check-version" element={<VersionCheckPage />} />
          
          {/* Services Routes */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/overview" element={<ServicesOverview />} />
          <Route path="/services/all" element={<AllServicesPage />} />
          
          {/* Legal Pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-conditions" element={<TermsConditionsPage />} />
          <Route path="/labor-intake" element={<LaborIntakeEntryPage />} />
          <Route path="/client/labor-case" element={<LaborIntakeEntryPage />} />
          <Route path="/labor-intake/new" element={<LaborIntakePage />} />
          <Route path="/labor-intake/draft" element={<LaborIntakeDraftPage />} />
          <Route path="/labor-intake/success" element={<LaborIntakeSuccessPage />} />
          <Route path="/labor-intake/track" element={<LaborIntakeTrackingPage />} />
          <Route path="/services/litigation" element={<LitigationPage />} />
          <Route path="/services/consultation" element={<ConsultationPage />} />
          <Route path="/services/contracts" element={<ContractsPage />} />
          <Route path="/services/notarization" element={<NotarizationPage />} />
          <Route path="/services/collection" element={<CollectionPage />} />
          <Route path="/services/cybercrime" element={<CybercrimePage />} />
          <Route path="/services/compliance" element={<CompliancePage />} />
          <Route path="/services/ip" element={<IPPage />} />
          <Route path="/services/franchise" element={<FranchisePage />} />

          {/* Packages Routes */}
          <Route path="/packages" element={<PackagesPage />} />
          <Route path="/packages/compare" element={<ComparePackagesPage />} />

          {/* Divisions Routes */}
          <Route path="/divisions" element={<DivisionsPage />} />
          <Route path="/divisions/:divisionSlug" element={<DivisionsPage />} />

          {/* User Type Selection Route */}
          <Route path="/select-user-type" element={<SelectUserTypePage />} />

          {/* Public Contract Verification Route */}
          <Route path="/verify/:contractId" element={<ContractVerificationPage />} />
          
          {/* Public Contract Signature Route - للعملاء بدون تسجيل دخول */}
          <Route path="/sign/:contractId" element={<PublicSignContractPage />} />
          
          {/* Services Page - صفحة الخدمات */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/خدماتنا" element={<ServicesPage />} />

          {/* Booking Routes */}
          <Route path="/book-consultation" element={<BookConsultationPage />} />
          <Route path="/register" element={<RegisterPage />} />
          
          {/* Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <ModernDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cases"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <CasesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cases/:caseId"
            element={
              <ProtectedRoute>
                <CaseDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <DocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/hr-old"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <HRManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/finance-old"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <FinanceManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tickets"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <TicketsManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <UsersManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/library"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <LegalLibrary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/correspondence"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <Correspondence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/content"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <ContentManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports-old"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <ReportsAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings-old"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <SystemSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/departments"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <DepartmentsManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/packages"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <PackagesManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles-permissions"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <RolesPermissionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/employee-permissions"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <EmployeePermissionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/custom-permissions"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <EmployeeCustomPermissionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cases-mgmt"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer', 'employee']}>
                <CasesManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contracts"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer']}>
                <ContractsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contracts/generate"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer']}>
                <ContractGenerationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contracts/templates"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer']}>
                <ContractTemplatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contracts/templates/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer']}>
                <ContractTemplateFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contracts/templates/:templateId/edit"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer']}>
                <ContractTemplateFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contracts/:contractId/sign"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer']}>
                <LawyerSignContractPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contracts/:contractId/audit-log"
            element={
              <ProtectedRoute allowedRoles={['admin', 'lawyer']}>
                <ContractAuditLogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsCenterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lawyer"
            element={
              <ProtectedRoute allowedRoles={['lawyer']}>
                <LawyerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <PermissionProtectedRoute 
                requiredPermission="ai_chat.access"
                allowedRoles={['admin', 'lawyer', 'employee']}
              >
                <ChatPage />
              </PermissionProtectedRoute>
            }
          />
          
          {/* Client Portal Routes */}
          <Route
            path="/client/dashboard"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/cases"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientCasesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/cases/:caseId"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientCaseDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/case/:caseId"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientCaseDetailsNewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/consultations"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientConsultationsPage />
              </ProtectedRoute>
            }
          />
          {/* Business Portal Routes */}
          <Route
            path="/business/dashboard"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <BusinessDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business/case/:caseId"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <BusinessCaseDetailsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Staff workspace is intentionally deferred until its standalone release. */}
          <Route path="/staff/*" element={<Navigate to="/admin/labor-intake" replace />} />
          
          <Route
            path="/client/tickets"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientTicketsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/contracts/:contractId/sign"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <SignContractPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/cases-advanced"
            element={
              <ProtectedRoute>
                <AdvancedCaseManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/case-details/:caseId"
            element={
              <ProtectedRoute>
                <CaseDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets"
            element={
              <ProtectedRoute>
                <TicketsManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets/create"
            element={
              <ProtectedRoute>
                <CreateTicketPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets/:ticketId"
            element={
              <ProtectedRoute>
                <TicketDetailsPage />
              </ProtectedRoute>
            }
          />
          {/* Old HR routes - moved to /admin/hr */}
          <Route
            path="/hr/requests"
            element={
              <ProtectedRoute>
                <RequestsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hr/requests/new"
            element={
              <ProtectedRoute>
                <CreateLeaveRequestPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hr/requests/:requestId"
            element={
              <ProtectedRoute>
                <RequestDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support/live-chats"
            element={
              <ProtectedRoute>
                <LiveChatsPage />
              </ProtectedRoute>
            }
          />

          {/* ========================================
              Admin Portal Routes - بوابة الإدارة
              (طبقة إدارية جديدة - لا تمس البوابات السابقة)
          ======================================== */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/cases/login" element={<LaborOperationsLoginPage />} />
          <Route path="/cases/workspace" element={<Navigate to="/admin/labor-intake" replace />} />
          <Route path="/cases/labor-intake" element={<Navigate to="/admin/labor-intake" replace />} />
          <Route path="/cases/labor-intake/:caseId" element={<Navigate to="/admin/labor-intake" replace />} />
          <Route path="/cases/operating-model" element={<Navigate to="/admin/labor-intake/operating-model" replace />} />
          <Route path="/cases/users" element={<Navigate to="/admin/labor-intake/users" replace />} />
          <Route path="/cases/lawyer" element={<Navigate to="/admin/labor-intake" replace />} />
          <Route path="/cases/settings" element={<Navigate to="/admin/labor-intake/settings" replace />} />
          <Route path="/dashboard/labor-cases/*" element={<Navigate to="/admin/labor-intake" replace />} />
          <Route path="/legal-operations/*" element={<Navigate to="/admin/labor-intake" replace />} />
          
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedAdminRoute>
                <AdminDashboardNew />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/labor-intake"
            element={
              <ProtectedLaborOperationsRoute>
                <LaborIntakeAdminPage />
              </ProtectedLaborOperationsRoute>
            }
          />
          <Route
            path="/admin/labor-intake/:caseId"
            element={
              <ProtectedLaborOperationsRoute>
                <LaborIntakeAdminDetailsPage />
              </ProtectedLaborOperationsRoute>
            }
          />
          <Route
            path="/admin/labor-intake/operating-model"
            element={
              <ProtectedLaborOperationsRoute>
                <LegalOperationsModelPage />
              </ProtectedLaborOperationsRoute>
            }
          />
          <Route
            path="/admin/labor-intake/users"
            element={
              <ProtectedLaborOperationsRoute>
                <LegalOperationsUsersPage />
              </ProtectedLaborOperationsRoute>
            }
          />
          <Route
            path="/admin/labor-intake/settings"
            element={
              <ProtectedLaborOperationsRoute>
                <LegalOperationsSettingsPage />
              </ProtectedLaborOperationsRoute>
            }
          />
          <Route
            path="/admin/labor-intake/messages"
            element={
              <ProtectedLaborOperationsRoute>
                <LegalOperationsMessagesPage />
              </ProtectedLaborOperationsRoute>
            }
          />

          {/* Legal Management - الإدارة القانونية */}
          <Route
            path="/admin/legal"
            element={
              <ProtectedAdminRoute requiredPermission="legal.view">
                <PlaceholderPage 
                  title="الإدارة القانونية"
                  titleEn="Legal Management"
                  icon={Scale}
                  description="إدارة شاملة لجميع القضايا والدعاوى القانونية"
                />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/legal/cases"
            element={
              <ProtectedAdminRoute requiredPermission="legal.view">
                <PlaceholderPage 
                  title="إدارة القضايا"
                  titleEn="Cases Management"
                  icon={Scale}
                  description="عرض شامل لجميع قضايا المنصة (أفراد + شركات) مع إمكانية التعيين والمتابعة"
                />
              </ProtectedAdminRoute>
            }
          />

          {/* CRM - إدارة العملاء */}
          <Route
            path="/admin/crm"
            element={
              <ProtectedAdminRoute requiredPermission="crm.view">
                <PlaceholderPage 
                  title="إدارة العملاء"
                  titleEn="CRM"
                  icon={Users}
                  description="نظام شامل لإدارة علاقات العملاء (أفراد وشركات)"
                />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/crm/individuals"
            element={
              <ProtectedAdminRoute requiredPermission="crm.view">
                <PlaceholderPage 
                  title="عملاء الأفراد"
                  titleEn="Individual Clients"
                  icon={Users}
                  description="قاعدة بيانات شاملة لجميع عملاء الأفراد مع سجل التواصل والعقود"
                />
              </ProtectedAdminRoute>
            }
          />

          {/* Finance - الإدارة المالية */}
          <Route
            path="/admin/finance"
            element={
              <ProtectedAdminRoute requiredPermission="finance.view">
                <PlaceholderPage 
                  title="الإدارة المالية"
                  titleEn="Finance"
                  icon={DollarSign}
                  description="إدارة شاملة للشؤون المالية والمحاسبية"
                />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/invoices"
            element={
              <ProtectedAdminRoute requiredPermission="finance.view">
                <InvoicesPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/invoices/create"
            element={
              <ProtectedAdminRoute requiredPermission="finance.create">
                <InvoiceFormPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/invoices/:invoiceId/edit"
            element={
              <ProtectedAdminRoute requiredPermission="finance.edit">
                <InvoiceFormPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/invoices/:invoiceId"
            element={
              <ProtectedAdminRoute requiredPermission="finance.view">
                <InvoiceDetailsPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/overdue-invoices"
            element={
              <ProtectedAdminRoute requiredPermission="finance.view">
                <OverdueInvoicesPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/creditors"
            element={
              <ProtectedAdminRoute requiredPermission="finance.creditors.view">
                <CreditorsPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/creditors/create"
            element={
              <ProtectedAdminRoute requiredPermission="finance.creditors.create">
                <CreditorFormPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/creditors/:creditorId/edit"
            element={
              <ProtectedAdminRoute requiredPermission="finance.creditors.edit">
                <CreditorFormPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/creditors/:creditorId"
            element={
              <ProtectedAdminRoute requiredPermission="finance.creditors.view">
                <CreditorDetailsPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/settings"
            element={
              <ProtectedAdminRoute requiredPermission="finance.settings">
                <InvoiceSettingsPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/revenues"
            element={
              <ProtectedAdminRoute requiredPermission="finance.revenue.view">
                <RevenuesPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/revenues/create"
            element={
              <ProtectedAdminRoute requiredPermission="finance.revenue.create">
                <RevenueFormPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/revenues/:revenueId/edit"
            element={
              <ProtectedAdminRoute requiredPermission="finance.revenue.edit">
                <RevenueFormPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/expenses"
            element={
              <ProtectedAdminRoute requiredPermission="finance.expense.view">
                <ExpensesPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/expenses/create"
            element={
              <ProtectedAdminRoute requiredPermission="finance.expense.create">
                <ExpenseFormPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/expenses/:expenseId/edit"
            element={
              <ProtectedAdminRoute requiredPermission="finance.expense.edit">
                <ExpenseFormPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/salaries"
            element={
              <ProtectedAdminRoute requiredPermission="finance.salary.view">
                <SalariesPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/salaries/create"
            element={
              <ProtectedAdminRoute requiredPermission="finance.salary.edit">
                <SalaryFormPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/salaries/batches"
            element={
              <ProtectedAdminRoute requiredPermission="finance.salary.view">
                <PayrollBatchesPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/salaries/:employeeId"
            element={
              <ProtectedAdminRoute requiredPermission="finance.salary.view">
                <SalaryDetailsPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/salaries/:salaryId/edit"
            element={
              <ProtectedAdminRoute requiredPermission="finance.salary.edit">
                <SalaryFormPage />
              </ProtectedAdminRoute>
            }
          />

          {/* Financial Reports - التقارير المالية */}
          <Route
            path="/admin/finance/reports"
            element={
              <ProtectedAdminRoute requiredPermission="finance.reports.view">
                <FinancialReportsPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/reports/revenues"
            element={
              <ProtectedAdminRoute requiredPermission="finance.reports.view">
                <RevenueReportsPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/reports/expenses"
            element={
              <ProtectedAdminRoute requiredPermission="finance.reports.view">
                <ExpenseReportsPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/reports/profitability"
            element={
              <ProtectedAdminRoute requiredPermission="finance.reports.view">
                <ProfitabilityReportsPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/reports/payroll"
            element={
              <ProtectedAdminRoute requiredPermission="finance.reports.view">
                <PayrollReportsPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/finance/reports/collection"
            element={
              <ProtectedAdminRoute requiredPermission="finance.reports.view">
                <CollectionReportsPage />
              </ProtectedAdminRoute>
            }
          />

          {/* HR - الموارد البشرية */}
          <Route
            path="/admin/hr"
            element={
              <ProtectedAdminRoute requiredPermission="hr.view">
                <EmployeesListPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/hr/employees"
            element={
              <ProtectedAdminRoute requiredPermission="hr.view">
                <EmployeesListPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/hr/employees/create"
            element={
              <ProtectedAdminRoute requiredPermission="hr.add">
                <EmployeeFormPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/hr/employees/:id"
            element={
              <ProtectedAdminRoute requiredPermission="hr.view">
                <EmployeeProfilePage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/hr/employees/:id/edit"
            element={
              <ProtectedAdminRoute requiredPermission="hr.edit">
                <EmployeeFormPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/hr/attendance"
            element={
              <ProtectedAdminRoute requiredPermission="hr.attendance.view">
                <AttendancePage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/hr/leaves"
            element={
              <ProtectedAdminRoute requiredPermission="hr.leave.view">
                <LeavesPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/hr/certificates"
            element={
              <ProtectedAdminRoute requiredPermission="hr.certificate.view">
                <CertificatesPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/hr/performance/reviews"
            element={
              <ProtectedAdminRoute requiredPermission="hr.performance.view">
                <PerformanceReviewsPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/hr/performance/reviews/form"
            element={
              <ProtectedAdminRoute requiredPermission="hr.performance.create">
                <PerformanceReviewFormPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/hr/performance/reviews/:id/edit"
            element={
              <ProtectedAdminRoute requiredPermission="hr.performance.edit">
                <PerformanceReviewFormPage />
              </ProtectedAdminRoute>
            }
          />

          {/* Support - الدعم الفني */}
          <Route
            path="/admin/support"
            element={
              <ProtectedAdminRoute requiredPermission="support.view">
                <PlaceholderPage 
                  title="الدعم الفني"
                  titleEn="Support"
                  icon={Headphones}
                  description="إدارة شاملة لخدمة العملاء والدعم الفني"
                />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/support/tickets"
            element={
              <ProtectedAdminRoute requiredPermission="support.view">
                <PlaceholderPage 
                  title="إدارة التذاكر"
                  titleEn="Tickets Management"
                  icon={Headphones}
                  description="نظام شامل لإدارة تذاكر الدعم الفني وطلبات العملاء"
                />
              </ProtectedAdminRoute>
            }
          />

          {/* Quality - الجودة والامتثال */}
          <Route
            path="/admin/quality"
            element={
              <ProtectedAdminRoute requiredPermission="quality.view">
                <PlaceholderPage 
                  title="الجودة والامتثال"
                  titleEn="Quality & Compliance"
                  icon={CheckCircle}
                  description="إدارة معايير الجودة والالتزام بالمعايير القانونية"
                />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/quality/policies"
            element={
              <ProtectedAdminRoute requiredPermission="quality.view">
                <PlaceholderPage 
                  title="السياسات والإجراءات"
                  titleEn="Policies & Procedures"
                  icon={CheckCircle}
                  description="إدارة السياسات الداخلية ونماذج العقود المعتمدة"
                />
              </ProtectedAdminRoute>
            }
          />

          {/* Operations - التشغيل الداخلي */}
          <Route
            path="/admin/operations"
            element={
              <ProtectedAdminRoute requiredPermission="operations.view">
                <PlaceholderPage 
                  title="التشغيل الداخلي"
                  titleEn="Operations"
                  icon={Cog}
                  description="إدارة العمليات الداخلية والتشغيل اليومي للمنصة"
                />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/operations/tasks"
            element={
              <ProtectedAdminRoute requiredPermission="operations.view">
                <PlaceholderPage 
                  title="المهام الداخلية"
                  titleEn="Internal Tasks"
                  icon={Cog}
                  description="إدارة المهام والمشاريع الداخلية للمكتب"
                />
              </ProtectedAdminRoute>
            }
          />

          {/* Reports - التقارير */}
          <Route
            path="/admin/reports"
            element={
              <ProtectedAdminRoute requiredPermission="reports.view">
                <PlaceholderPage 
                  title="التقارير الذكية"
                  titleEn="Smart Reports"
                  icon={BarChart3}
                  description="تقارير تحليلية شاملة عن الأداء والإحصائيات"
                />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/reports/executive"
            element={
              <ProtectedAdminRoute requiredPermission="reports.view">
                <PlaceholderPage 
                  title="التقارير التنفيذية"
                  titleEn="Executive Reports"
                  icon={BarChart3}
                  description="تقارير شاملة عن أداء المنصة وجميع الأقسام"
                />
              </ProtectedAdminRoute>
            }
          />

          {/* Settings - الإعدادات */}
          <Route
            path="/admin/settings"
            element={
              <ProtectedAdminRoute requiredPermission="settings.view">
                <PlaceholderPage 
                  title="الإعدادات"
                  titleEn="Settings"
                  icon={Settings}
                  description="إعدادات النظام والتخصيصات العامة"
                />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/settings/roles"
            element={
              <ProtectedAdminRoute requiredPermission="settings.view">
                <PlaceholderPage 
                  title="الأدوار والصلاحيات"
                  titleEn="Roles & Permissions"
                  icon={Settings}
                  description="إدارة الأدوار وتعيين الصلاحيات لكل دور"
                />
              </ProtectedAdminRoute>
            }
          />

        </Routes>
        <LiveChatWidget />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
    </AdminAuthProvider>
  );
}

export default App;
